import { AsyncPipe, DecimalPipe, NgClass } from '@angular/common'
import { Component, input, OnDestroy, OnInit } from '@angular/core'
import { BehaviorSubject, map } from 'rxjs'

import { Bet } from '../../../../pages/my-bets/types/my-bets.type'
import { BetService } from '../../../services/bet.service'
import { MarketService } from '../../../services/market.service'
import { Runner } from '../../../types/market.type'
import { SelectedRunner } from '../../place-bet-modal/types/selected-runner.type'
import { PlaceBetModalService } from '../../place-bet-modal/place-bet-modal.service'
import { LadderCondition, PnLOutcome } from '../types/ahc-pnl.type'

@Component({
	selector: 'app-ah-ladder-pnl',

	standalone: true,
	imports: [NgClass, DecimalPipe, AsyncPipe],
	templateUrl: './ah-ladder-pnl.component.html',
	styleUrl: './ah-ladder-pnl.component.scss',
})
export class AhLadderPnlComponent implements OnInit, OnDestroy {
	bet = input<Bet>()

	conditionalPnLs$ = new BehaviorSubject<PnLOutcome[]>([])

	constructor(
		protected readonly betService: BetService,
		protected readonly placeBetModalService: PlaceBetModalService,
		protected readonly marketService: MarketService,
	) {}

	ngOnInit(): void {
		this.getCurrentPlacement()

		const bet = this.bet()
		if (bet) {
			this.getMarketForBet(bet)
		}
	}

	getMarketForBet(bet: Bet) {
		this.betService.betsMarkets$.subscribe((markets) => {
			const market = markets[bet.marketId]
			const { marketName, handicap, side, sizeMatched, priceSize, averagePriceMatched, betSize, selectionId } = bet || {}

			if (market) {
				const betOrder = {
					HANDICAP: handicap,
					betRunner: market.runners.find((runner) => runner.selectionId === selectionId && runner.handicap === handicap),
					runners: market.runners,
					SIDE: side,
					price: Number(sizeMatched) === 0 ? priceSize.price : averagePriceMatched,
					stake: Number(betSize),
					SPORT_ID: market.eventType.id,
				}

				if (['Handicap', 'Asian Handicap'].includes(marketName)) {
					this.determineAHCPnL(betOrder)
				} else {
					this.determineGLPnL(betOrder)
				}
			}
		})
	}

	getCurrentPlacement() {
		this.placeBetModalService.currentPlacement$
			.pipe(
				map((currentPlacement) => {
					if (currentPlacement) {
						if (['Handicap', 'Asian Handicap'].includes(currentPlacement.oddMarketName)) {
							this.determineAHCPnL(currentPlacement)
						} else {
							this.determineGLPnL(currentPlacement)
						}
					}
				}),
			)
			.subscribe()
	}

	determineAHCPnL(placement: Partial<SelectedRunner>) {
		const conditions = this.formulateACHConditions(placement)
		const { side, handicap, handicapValues, stake: pStake, fOdds } = placement || {}

		if (typeof handicap !== 'number' || !handicapValues?.length || !pStake || !fOdds) {
			this.conditionalPnLs$.next([])
			return
		}

		const isBack = side === 'BACK'
		const isNegative = handicap < 0
		const isStakeSplit = handicapValues?.length === 2

		const stake = isStakeSplit ? pStake / 2 : pStake
		const calculatedPnL = (fOdds - 1) * stake
		const profit = isBack ? calculatedPnL : stake
		const loss = isBack ? stake : calculatedPnL

		const conditionalPnLs: PnLOutcome[] = conditions?.map((condition: LadderCondition) => {
			let pnl = 0
			const { goals } = condition || {}
			handicapValues?.forEach((value: number) => {
				const absHcpValue = Math.abs(value)
				if (isNegative) {
					if ((goals && goals < absHcpValue) || !goals) {
						pnl += isBack ? -loss : profit
					} else if (goals > absHcpValue) {
						pnl += isBack ? profit : -loss
					}
				} else {
					if ((goals && goals < absHcpValue) || !goals) {
						pnl += isBack ? profit : -loss
					} else if (goals > absHcpValue) {
						pnl += isBack ? -loss : profit
					}
				}
			})

			return { ...condition, pnl }
		})

		this.conditionalPnLs$.next(conditionalPnLs)
	}

	determineGLPnL(placement: Partial<SelectedRunner>) {
		const conditions = this.formulateGLConditions(placement)
		const { side, name, handicapValues, fOdds, stake: pStake } = placement || {}

		if (!handicapValues?.length || !pStake || !fOdds) {
			this.conditionalPnLs$.next([])
			return
		}

		const isBack = side === 'BACK'
		const isOver = name?.includes('Over')
		const isStakeSplit = handicapValues?.length === 2

		const stake = isStakeSplit ? pStake / 2 : pStake
		const calculatedPnL = (fOdds - 1) * stake
		const profit = isBack ? calculatedPnL : stake
		const loss = isBack ? stake : calculatedPnL

		const conditionalPnLs = conditions?.map((condition: LadderCondition) => {
			let pnl = 0
			const { goals } = condition || {}
			handicapValues?.forEach((value: number) => {
				const absHcpValue = Math.abs(value)
				if (isOver) {
					if ((goals && goals < absHcpValue) || !goals) {
						pnl += isBack ? -loss : profit
					} else if (goals > absHcpValue) {
						pnl += isBack ? profit : -loss
					}
				} else {
					if ((goals && goals < absHcpValue) || !goals) {
						pnl += isBack ? profit : -loss
					} else if (goals > absHcpValue) {
						pnl += isBack ? -loss : profit
					}
				}
			})

			return { ...condition, pnl }
		})

		this.conditionalPnLs$.next(conditionalPnLs)
	}

	formulateACHConditions(placement: Partial<SelectedRunner>): LadderCondition[] {
		const { handicap, name: placementRunnerName, handicapValues, runners } = placement || {}
		if (typeof handicap !== 'number') {
			return []
		}
		const isNegative = handicap < 0

		const otherRunner = runners?.find((runner: Runner) => runner?.runnerName !== placementRunnerName)

		const absHandicaps = handicapValues?.map(Math.abs) || []
		const maxHcp = absHandicaps.length ? Math.max(...absHandicaps) : 0
		const minHcp = absHandicaps.length ? Math.min(...absHandicaps) : 0

		const maxRange = Number.isInteger(maxHcp) ? maxHcp + 1 : Math.ceil(maxHcp)
		const minRange = Math.ceil(minHcp)

		const runnerLabel = isNegative ? placementRunnerName : otherRunner?.runnerName
		const conditions: LadderCondition[] = []

		for (let i = -1; i <= maxRange; i++) {
			if (minRange - i === 1) {
				const winText = minRange > 1 ? `by ${minRange} or more` : ''
				conditions.push({
					label: `If ${runnerLabel} do not win ${winText}`.trim(),
				})
			} else if (i >= minRange && (minRange !== 0 || i !== 0)) {
				const moreText = i === maxRange ? 'or more' : ''
				conditions.push({
					label: `If ${runnerLabel} win by ${i} ${moreText}`.trim(),
					goals: i,
				})
			}
		}

		return conditions
	}

	formulateGLConditions(placement: Partial<SelectedRunner>) {
		const { handicapValues, sportId } = placement || {}

		const maxHcp = handicapValues?.length ? Math.max(...handicapValues) : 0
		const minHcp = handicapValues?.length ? Math.min(...handicapValues) : 0

		const maxRange = Number.isInteger(maxHcp) ? maxHcp + 1 : Math.ceil(maxHcp)
		const minRange = Math.ceil(minHcp)

		const conditions = []

		const scoreType = sportId === '7522' ? 'point' : 'goal'

		for (let i = 0; i <= maxRange; i++) {
			const isExactOneBelowMin = minRange - i === 1
			const isValidGoal = i >= minRange && (minRange !== 0 || i !== 0)

			if (isExactOneBelowMin) {
				const winText = minRange > 1 ? 'or less' : ''
				conditions.push({
					label: i === 0 ? `Exactly ${i} ${scoreType}s` : `${i} ${scoreType}s ${winText}`,
				})
			} else if (isValidGoal) {
				conditions.push({
					label: i === maxRange ? `${i} ${scoreType}s or more` : `Exactly ${i} ${scoreType}${i > 1 ? 's' : ''}`,
					goals: i,
				})
			}
		}

		return conditions
	}

	resetPnL() {
		this.placeBetModalService.currentPlacement$.next(null)
		this.conditionalPnLs$.next([])
	}

	ngOnDestroy() {
		this.resetPnL()
	}
}
